// Voice for Atena: dictation in, speech out.
//
// Two browser APIs, with very different properties, and the difference is the
// user's business rather than an implementation detail:
//
//   speechSynthesis      speaks locally. No audio leaves the machine.
//   SpeechRecognition    in Chrome and Edge, sends audio to the browser
//                        vendor's servers. This is a self-hosted product, so
//                        dictation is opt-in and the panel says where the audio
//                        goes. Firefox does not implement it at all, and the
//                        microphone simply does not appear there.
//
// The pure parts live here so they can be tested without a microphone.

export const WAKE_WORD = "atena";

export function recognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function canListen() {
  return Boolean(recognitionCtor());
}

export function canSpeak() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

// Markdown is written to be read with the eyes. Spoken literally it becomes
// "asterisc asterisc Pot doua puncte asterisc asterisc", so the marks come out
// before the text reaches the synthesiser.
export function speakable(markdown) {
  if (!markdown) return "";
  return String(markdown)
    .replace(/```[\s\S]*?```/g, " Un bloc de cod. ")   // never read code aloud
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")           // link text, not the URL
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\*\*([^*]*)\*\*/g, "$1")
    .replace(/(^|\s)[*_]([^*_]+)[*_](?=\s|$)/g, "$1$2")
    .replace(/^\s*[-•*]\s+/gm, "")
    .replace(/^\s*\|.*\|\s*$/gm, " ")                  // a table read aloud is noise
    .replace(/[«»"]/g, "")
    .replace(/\s*\n\s*/g, ". ")
    .replace(/\.\s*\.+/g, ".")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// "Atena, ce tichete sunt deschise?" -> "ce tichete sunt deschise?"
// Anything not addressed to her returns null, so hands-free mode does not answer
// a conversation happening in the room.
export function afterWakeWord(transcript, wake = WAKE_WORD) {
  const text = String(transcript || "").trim();
  if (!text) return null;
  const flat = text.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
  const at = flat.indexOf(wake);
  if (at === -1) return null;
  const rest = text.slice(at + wake.length).replace(/^[\s,.:;!?-]+/, "").trim();
  return rest || null;
}

// A voice that speaks the language of the answer, if the system has one.
// Romanian is not installed everywhere; falling back to the default voice reads
// Romanian with an English accent, which is worse than nothing only if it is
// unintelligible -- it is not, so the fallback stands.
export function pickVoice(voices, lang = "ro-RO") {
  const list = voices || [];
  const base = lang.split("-")[0];
  return list.find((v) => v.lang === lang)
      || list.find((v) => (v.lang || "").startsWith(base))
      || list.find((v) => v.default)
      || list[0]
      || null;
}
